import { useState, useEffect } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session with error handling
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          // Si hay error, limpiar la sesión local
          setSession(null);
          setUser(null);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // En caso de error de red, limpiar sesión
        setSession(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes with error handling
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event);
      
      if (event === 'TOKEN_REFRESHED') {
        console.log('Token renovado exitosamente');
      } else if (event === 'SIGNED_OUT') {
        console.log('Sesión cerrada');
        setSession(null);
        setUser(null);
      } else if (event === 'SIGNED_IN') {
        console.log('Usuario autenticado');
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, name: string) => {
    try {
      // A dónde vuelve la persona al tocar el enlace del correo.
      //
      // Se arma con la dirección desde la que se está usando la app, no
      // escrita a mano. Antes estaba fija apuntando a la dirección vieja
      // y, si no coincidía, mandaba a Readdy —la plataforma que ya no se
      // usa— y la confirmación terminaba en una versión rota.
      //
      // Así funciona igual en app.sospadres.info, en la dirección vieja
      // y en la computadora mientras se programa, sin tocar nada.
      const redirectUrl = `${window.location.origin}/auth/confirm`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
          },
          emailRedirectTo: redirectUrl,
        },
      });
      return { data, error };
    } catch (error: any) {
      console.error('Error in signUp:', error);
      return { data: null, error: { message: 'Error de conexión. Por favor, verifica tu internet.' } };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { data, error };
    } catch (error: any) {
      console.error('Error in signIn:', error);
      return { data: null, error: { message: 'Error de conexión. Por favor, verifica tu internet.' } };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          skipBrowserRedirect: false,
        },
      });
      return { data, error };
    } catch (error: any) {
      console.error('Error in signInWithGoogle:', error);
      return { data: null, error: { message: 'Error de conexión. Por favor, verifica tu internet.' } };
    }
  };

  const signInWithFacebook = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });
      return { data, error };
    } catch (error: any) {
      console.error('Error in signInWithFacebook:', error);
      return { data: null, error: { message: 'Error de conexión. Por favor, verifica tu internet.' } };
    }
  };

  const signOut = async () => {
    try {
      // Primero verificar si hay una sesión activa
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        // Solo intentar cerrar sesión si existe una sesión válida
        const { error } = await supabase.auth.signOut();
        if (error && error.message !== 'Auth session missing!') {
          console.error('Error signing out:', error);
          // No lanzar error, solo registrar
        }
      }

      // Limpiar estado local independientemente del resultado
      setSession(null);
      setUser(null);

      return { error: null };
    } catch (error) {
      console.error('Error during sign out:', error);

      // Limpiar estado local incluso si hay error
      setSession(null);
      setUser(null);

      // No devolver error para permitir que la navegación continúe
      return { error: null };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      // A dónde lleva el enlace del correo. Sin esto iba a la portada,
      // que no pide contraseña nueva: la persona tocaba el enlace, veía
      // la pantalla de siempre y se quedaba afuera igual.
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/recuperar`,
      });
      return { data, error };
    } catch (error: any) {
      console.error('Error in resetPassword:', error);
      return { data: null, error: { message: 'Error de conexión. Por favor, verifica tu internet.' } };
    }
  };

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    signOut,
    resetPassword,
  };
};